/* display-template.js
 *
 * display-template-a: 2 col - no images, filters out disabled items
 * display-template-b: 4 col - image + name
 * display-template-c: 3 col - no image options
 *
 */

/* --- shared props & helpers --- */
const XpvSharedProps = {
  label: String,
  attribute: Object,
  description: String,
  value: [String, Number, null],
  items: Array,
  loading: Boolean,
  size: String,
  rounded: String,
  node_id: Number
};

const XpvSelectionMixin = {
  data() {
    return { selected: this.value, hovering: null };
  },
  watch: {
    value(v) { this.selected = v; },
    selected(v) { this.$emit('input', v); }
  },
  methods: {
    selectOption(id) { this.selected = id; },
    isSelected(item) { return String(this.selected) === String(item.id); },
    stateClass(item) {
      return {
        'is-selected': this.isSelected(item),
        'is-hovered': this.hovering === item.id && !item.disabled,
        'is-disabled': !!item.disabled
      };
    }
  }
};

/* === A: 2 col - no images, filters out disabled items === */
window.addEventListener('load', function () {
  const DisplayTemplateA = {
    props: XpvSharedProps,
    mixins: [XpvSelectionMixin],
    data() {
      return {
        descriptionHtml: (this.attribute && this.attribute.description) || this.description || ''
      };
    },
    watch: {
      attribute(n) { this.descriptionHtml = (n && n.description) || this.description || ''; },
      description(n) { if (!this.attribute || !this.attribute.description) this.descriptionHtml = n || ''; }
    },
    computed: {
      visibleItems() {
        return this.items || [];
      }
    },
    template: `
      <div class="mb-6">
        <p class="xpv-label text-gray-600 text-base mb-2">{{ label }}</p>
        <p v-if="descriptionHtml" class="xpv-description text-gray-500 text-sm mb-3" v-html="descriptionHtml"></p>
        <div class="grid gap-3 w-full" style="grid-template-columns: repeat(2, minmax(0, 1fr));">
          <button
            v-for="item in visibleItems"
            :key="item.id"
            @mouseenter="!item.disabled && (hovering = item.id)"
            @mouseleave="hovering = null"
            @click="!item.disabled && selectOption(item.id)"
            :disabled="item.disabled"
            class="relative text-base font-semibold transition-all duration-200 ease-in-out transform rounded cursor-pointer"
            :style="{
              minWidth: '80px',
              padding: '0.75rem 1rem',
              borderRadius: '0.375rem',
              textAlign: 'center',
              display: 'inline-block',
              backgroundColor: selected === item.id ? 'var(--xpv-bg-selected)' : 'var(--xpv-white)',
              border: selected === item.id
                ? '1px solid var(--xpv-primary)'
                : hovering === item.id && !item.disabled
                  ? '1px solid var(--xpv-primary-hover)'
                  : '1px solid var(--xpv-gray-300)',
              color: item.disabled ? 'var(--xpv-gray-400)' : 'var(--xpv-black)',
              opacity: item.disabled ? 0.4 : 1,
              cursor: item.disabled ? 'not-allowed' : 'pointer'
            }"
          >
            <span class="xpv-item-label">{{ item.label }}</span>

            <div
              v-if="selected === item.id"
              class="absolute top-2 right-2 w-5 h-5 rounded-full bg-orange-500 text-white text-sm flex items-center justify-center transition-opacity duration-200 ease-in-out z-50"
            >
              ✓
            </div>
          </button>
        </div>
      </div>
    `
  };

  ExpiviOptionsVueInstance.prototype.$customViews['display-template-a'] = DisplayTemplateA;
});

/* === B: image + name === */
window.addEventListener('load', function () {
  const DisplayTemplateB = {
    props: XpvSharedProps,
    mixins: [XpvSelectionMixin],
    data() {
      return {
        descriptionHtml: (this.attribute && this.attribute.description) || this.description || ''
      };
    },
    watch: {
      attribute(n) { this.descriptionHtml = (n && n.description) || this.description || ''; },
      description(n) { if (!this.attribute || !this.attribute.description) this.descriptionHtml = n || ''; }
    },
    template: `
      <div>
        <p class="xpv-label text-gray-600 text-base mb-2">{{ label }}</p>
        <p v-if="descriptionHtml" class="xpv-description text-gray-500 text-sm mb-3" v-html="descriptionHtml"></p>
        <div class="grid gap-3 w-full grid-cols-3 md:!grid-cols-4">
          <button
            v-for="item in items"
            :key="item.id"
            @mouseenter="!item.disabled && (hovering = item.id)"
            @mouseleave="hovering = null"
            @click="!item.disabled && selectOption(item.id)"
            :disabled="item.disabled"
            class="rounded relative cursor-pointer flex flex-col justify-start h-full transition-all duration-200"
            :style="{
              border: selected === item.id
                ? '1px solid var(--xpv-primary)'
                : hovering === item.id && !item.disabled
                  ? '1px solid var(--xpv-primary-hover)'
                  : '1px solid var(--xpv-gray-300)',
              backgroundColor: 'var(--xpv-white)',
              transform: selected === item.id ? 'scale(1.02)' : 'scale(1)',
              color: item.disabled ? 'var(--xpv-gray-400)' : 'var(--xpv-black)',
              opacity: item.disabled ? 0.4 : 1,
              cursor: item.disabled ? 'not-allowed' : 'pointer'
            }"
          >
            <img :src="item.thumbnail" alt="" class="w-full aspect-square object-cover" />
            <div class="xpv-item-label text-center py-2 text-sm">{{ item.label }}</div>

            <div
              v-if="selected === item.id"
              class="absolute top-2 right-2 w-5 h-5 rounded-full bg-orange-500 text-white text-sm flex items-center justify-center z-50"
            >
              ✓
            </div>
          </button>
        </div>
      </div>
    `
  };

  ExpiviOptionsVueInstance.prototype.$customViews['display-template-b'] = DisplayTemplateB;
});

/* === C: 3 col - no image options === */
window.addEventListener('load', function () {
  const DisplayTemplateC = {
    props: XpvSharedProps,
    mixins: [XpvSelectionMixin],
    data() {
      return {
        descriptionHtml: (this.attribute && this.attribute.description) || this.description || ''
      };
    },
    watch: {
      attribute(n) { this.descriptionHtml = (n && n.description) || this.description || ''; },
      description(n) { if (!this.attribute || !this.attribute.description) this.descriptionHtml = n || ''; }
    },
    template: `
      <div class="mb-6">
        <p class="xpv-label text-gray-600 text-base mb-2">{{ label }}</p>
        <p v-if="descriptionHtml" class="xpv-description text-gray-500 text-sm mb-3" v-html="descriptionHtml"></p>
        <div class="grid gap-3 w-full grid-cols-2 md:!grid-cols-3">
          <button
            v-for="item in items"
            :key="item.id"
            @mouseenter="!item.disabled && (hovering = item.id)"
            @mouseleave="hovering = null"
            @click="!item.disabled && selectOption(item.id)"
            :disabled="item.disabled"
            class="relative text-base font-semibold text-black transition-all duration-200 ease-in-out transform rounded cursor-pointer"
            :style="{
              minWidth: '80px',
              padding: '0.75rem 1rem',
              borderRadius: '0.375rem',
              textAlign: 'center',
              display: 'inline-block',
              backgroundColor: selected === item.id ? 'var(--xpv-bg-selected)' : 'var(--xpv-white)',
              border: selected === item.id
                ? '1px solid var(--xpv-primary)'
                : hovering === item.id && !item.disabled
                  ? '1px solid var(--xpv-primary-hover)'
                  : '1px solid var(--xpv-gray-300)',
              color: item.disabled ? 'var(--xpv-gray-400)' : 'var(--xpv-black)',
              opacity: item.disabled ? 0.4 : 1,
              cursor: item.disabled ? 'not-allowed' : 'pointer'
            }"
          >
            <span class="xpv-item-label">{{ item.label }}</span>

            <div
              v-if="selected === item.id"
              class="absolute top-2 right-2 w-5 h-5 rounded-full bg-orange-500 text-white text-sm flex items-center justify-center transition-opacity duration-200 ease-in-out z-50"
            >
              ✓
            </div>
          </button>
        </div>
      </div>
    `
  };

  ExpiviOptionsVueInstance.prototype.$customViews['display-template-c'] = DisplayTemplateC;
});